// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import {
    getFirestore,
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
} from "firebase/firestore";
import type { Firestore } from "firebase/firestore";

import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getAuth } from "firebase/auth";

import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { getAnalytics, setUserId } from "firebase/analytics";
import RemoteConfigService from "./remoteConfig";
import {
    contactStudyFromAccount,
    enrollStudyForUser,
    saveStudyToAccount,
} from "./components/StudyCard/utils/formSubmission";
import { PostSignupAction, PostSignupNotification } from "./utils/localStorageWrapper";

const remoteConfig = await RemoteConfigService.Instance();

// Initialize Firebase
const app = initializeApp(remoteConfig.firebaseOptions);

export const auth = getAuth(app); /** DO NOT USE. GET THIS FROM THE CONTEXT. OKAY? OKAY. */
export const analytics = getAnalytics(app);
export const functions = getFunctions(app);

if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG;
}

export const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(remoteConfig.recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
});

type EnvValue = string | boolean | undefined;

const readEnvValue = (variableName: string): string | undefined => {
    try {
        const metaEnv = (import.meta as ImportMeta).env as Record<string, EnvValue>;
        const metaValue = metaEnv?.[variableName];
        if (typeof metaValue !== "undefined") {
            return String(metaValue);
        }
    } catch (error) {
        // Ignore error; process.env handling below covers server-side.
    }

    if (typeof process !== "undefined" && process.env) {
        const processValue = process.env[variableName];
        if (typeof processValue !== "undefined") {
            return String(processValue);
        }
    }

    return undefined;
};

const shouldUseFunctionsEmulator = (): boolean => {
    const flag = readEnvValue("VITE_USE_FUNCTIONS_EMULATOR");
    return flag === "true" || flag === "1";
};

if (shouldUseFunctionsEmulator()) {
    const host = readEnvValue("VITE_FUNCTIONS_EMULATOR_HOST") ?? "127.0.0.1";
    const port = Number(readEnvValue("VITE_FUNCTIONS_EMULATOR_PORT") ?? 5001);
    connectFunctionsEmulator(functions, host, port);
}

export const db = initializeFirestore(app, {
    ignoreUndefinedProperties: true,
    localCache: persistentLocalCache(/*settings*/ { tabManager: persistentMultipleTabManager() }),
});
/**
 * Initialize Firestore database, this function should be used over getFirestore() from firebase/firestore
 * to ensure that the database is initialized with the correct settings.
 *
 * @returns {Firestore} The Firestore database instance.
 */
export function getFirestoreDB(): Firestore {
    // Export Firestore database
    // Prevent undefined properties from throwing an error
    return getFirestore();
}

// Load Google Maps
setOptions({ key: remoteConfig.mapsAPIKey });

export async function getMapsLoader(): Promise<unknown> {
    const places = importLibrary("places");
    const geocoding = importLibrary("geocoding");

    return Promise.all([places, geocoding]);
}

auth.onAuthStateChanged((currentUser) => {
    if (currentUser !== null) {
        setUserId(analytics, currentUser.uid);
    } else {
        setUserId(analytics, null);
    }
});

auth.onAuthStateChanged(async (currentUser) => {
    if (currentUser) {
        console.debug("User signed in:", currentUser.uid);
        currentUser.getIdToken().then(async (idToken) => {
            document.cookie = `session=${idToken};path=/;max-age=${3600 * 24 * 60}`; // 60 days
        });
    }

    if (currentUser && !currentUser.isAnonymous) {
        // Theres some kind of race condition going on here
        const action = PostSignupAction.get();
        if (action) {
            const [type, nctId] = action.split("-");
            if (type && nctId) {
                try {
                    if (type === "save") {
                        await saveStudyToAccount({
                            nctID: nctId,
                            uid: currentUser.uid,
                            isAnonymous: currentUser.isAnonymous,
                        });
                    } else if (type === "contact") {
                        await contactStudyFromAccount({
                            nctID: nctId,
                            uid: currentUser.uid,
                            isAnonymous: currentUser.isAnonymous,
                        });
                    } else if (type === "enroll") {
                        await enrollStudyForUser(currentUser.uid, nctId);
                    }
                    PostSignupNotification.set(action);
                } catch (error) {
                    console.error(error);
                }
                PostSignupAction.clear();
            }
        }
    }
    // User is signed in
});
